import { NavLink, useNavigate } from "react-router-dom";
import { t } from "ttag";

import { useLogout } from "@/app/hooks";
import { useMessenger } from "@/app/hooks/use-messenger";
import { PORTAL_USER_TYPE } from "@/app/models";
import { ROUTES } from "@/app/router";
import { usePortalUserStore } from "@/app/stores";
import { Logo } from "@/assets";
import { tw } from "@/shared/utils";
import { NeedHelpMenu } from "./components";
import { getNavigation } from "./utils";

export const Sidebar = ({
  onCloseSidebar,
}: {
  onCloseSidebar?: () => void;
}) => {
  const user = usePortalUserStore((state) => state.portalUser);

  const canEditProfile = user?.type === PORTAL_USER_TYPE.PATIENT;

  const navigation = getNavigation(user);

  const logoutMutation = useLogout();

  const navigate = useNavigate();

  const {
    canOpenMessenger,
    actions: { toggleMessengerVisibility },
  } = useMessenger();

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  const handleContactSupport = () => {
    onCloseSidebar?.();
    toggleMessengerVisibility();
  };

  return (
    <div className="relative z-20 flex h-dvh w-67 flex-col gap-8 overflow-y-auto border border-primary-100 bg-primary-50 px-10 py-10">
      <Logo
        className="h-6 w-31"
        onClick={() => navigate(ROUTES.BASE)}
        role="button"
      />

      {user && (
        <nav className="grow">
          <ul className="flex h-full flex-col gap-6 font-medium text-neutral-950">
            <li className="grow">
              <ul className="flex flex-col gap-2">
                {navigation.map((item) => (
                  <li key={item.label}>
                    {!item.disabled &&
                    (user.subscriptions.some(
                      ({
                        status,
                        product: { category },
                        intakeFormCompleted,
                      }) =>
                        item.subscriptionStatus.includes(status) &&
                        item.activeCategories.includes(category.slug) &&
                        (!item.needsIntakeForm || intakeFormCompleted),
                    ) ||
                      !user.subscriptions.length) ? (
                      <NavLink
                        to={item.path}
                        onClick={onCloseSidebar}
                        draggable="false"
                        className={({ isActive }) =>
                          tw(
                            "flex items-center gap-2 rounded-lg p-3 transition-all duration-100",
                            isActive && "bg-primary-100",
                            !isActive &&
                              "hover:bg-primary-200/50 active:bg-primary-200/80",
                          )
                        }
                        target={item.external ? "_blank" : "_self"}
                        rel={item.external ? "noopener noreferrer" : ""}
                      >
                        {item.label}
                      </NavLink>
                    ) : (
                      <span className="block p-2.5 text-neutral-600 select-none">
                        {item.label}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </li>

            <li>
              <ul className="flex flex-col gap-1">
                <li>
                  {canEditProfile ? (
                    <NavLink
                      to={ROUTES.PROFILE.OVERVIEW}
                      onClick={onCloseSidebar}
                      className={({ isActive }) =>
                        tw(
                          "flex items-center gap-2 rounded-lg p-3 transition-all duration-100",
                          isActive && "bg-primary-100",
                          !isActive &&
                            "hover:bg-primary-200/50 active:bg-primary-200/80",
                        )
                      }
                    >
                      <span
                        className="overflow-hidden text-ellipsis"
                        aria-hidden="true"
                      >
                        {t`Profile`}
                      </span>
                    </NavLink>
                  ) : (
                    <div className="flex items-center gap-1 truncate rounded-lg py-2.5">
                      <span
                        className="overflow-hidden text-ellipsis"
                        aria-hidden="true"
                      >
                        {user.name}
                      </span>
                    </div>
                  )}
                </li>

                {user.type !== PORTAL_USER_TYPE.ADMIN && (
                  <>
                    <li>
                      <NeedHelpMenu />
                    </li>

                    {canOpenMessenger && (
                      <li className="rounded-lg">
                        <button
                          type="button"
                          className="p-2.5 text-left hover:underline"
                          onClick={handleContactSupport}
                        >
                          {t`Messages`}
                        </button>
                      </li>
                    )}
                  </>
                )}

                <hr className="my-2.5 border-neutral-100" />

                <li>
                  <button
                    type="button"
                    className="p-2.5 text-left hover:underline"
                    onClick={handleLogout}
                  >
                    {t`Log out`}
                  </button>
                </li>
              </ul>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};
